import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import TopicSelector from '../components/lecture/TopicSelector';
import TopicDetail from '../components/lecture/TopicDetail';
import LectureGrid from '../components/lecture/LectureGrid';
import VideoLightbox from '../components/lecture/VideoLightbox';
import './LecturesPage.css';

export default function LecturesPage() {
  // Initialize the topics state with an empty array
  const [topics, setTopics] = useState([]);
  const [selectedTopicId, setSelectedTopicId] = useState(null);
  const [lectures, setLectures] = useState([]);
  const [loadingTopics, setLoadingTopics] = useState(true);
  const [loadingLectures, setLoadingLectures] = useState(false);
  const [error, setError] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoadingTopics(true);
    api
      .get('/api/lectures/topics')
      .then((res) => {
        if (cancelled) return;
        const list = res.data?.topics || [];
        setTopics(list);
        if (list.length > 0) {
          setSelectedTopicId(list[0].id);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load topics:', err);
        setError('Failed to load topics. Please try again later.');
      })
      .finally(() => {
        if (!cancelled) setLoadingTopics(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedTopicId) {
      setLectures([]);
      return;
    }
    let cancelled = false;
    setLoadingLectures(true);
    api
      .get('/api/lectures/by-topic/' + selectedTopicId)
      .then((res) => {
        if (cancelled) return;
        const lectures = res.data?.lectures || [];
        // Sort lectures by lectureDate in ascending order (oldest first)
        const sortedLectures = [...lectures].sort((a, b) => {
          const dateA = a.lectureDate ? new Date(a.lectureDate).getTime() : 0;
          const dateB = b.lectureDate ? new Date(b.lectureDate).getTime() : 0;
          return dateA - dateB;
        });
        setLectures(sortedLectures);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load lectures:', err);
        setLectures([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingLectures(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedTopicId]);

  const selectedTopic = topics.find((t) => t.id === selectedTopicId) || null;

  const handleTopicSelect = useCallback((topicId) => {
    setSelectedTopicId(topicId);
    setLightboxOpen(false);
  }, []);

  const handleLectureClick = useCallback((index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  }, []);

  const handleLightboxClose = useCallback(() => {
    setLightboxOpen(false);
  }, []);

  const handleLightboxPrev = useCallback(() => {
    setLightboxIndex((i) => (i > 0 ? i - 1 : lectures.length - 1));
  }, [lectures.length]);

  const handleLightboxNext = useCallback(() => {
    setLightboxIndex((i) => (i < lectures.length - 1 ? i + 1 : 0));
  }, [lectures.length]);

  if (loadingTopics) {
    return (
      <div className="lectures-page">
        <header className="lectures-page-header">
          <h1>Lectures</h1>
          <p>Explore spiritual discourses by topic</p>
        </header>
        <div className="lectures-page-layout">
          <aside className="lectures-page-sidebar">
            <div className="skeleton skeleton-title" style={{ height: '2rem', width: '60%', marginBottom: '1rem' }} />
            <div className="skeleton skeleton-row" style={{ height: '2.5rem', marginBottom: '0.5rem' }} />
            <div className="skeleton skeleton-row" style={{ height: '2.5rem', marginBottom: '0.5rem' }} />
            <div className="skeleton skeleton-row" style={{ height: '2.5rem', marginBottom: '0.5rem' }} />
          </aside>
          <main className="lectures-page-main">
            <div className="skeleton skeleton-title" style={{ height: '2rem', width: '50%', marginBottom: '1rem' }} />
            <div className="skeleton skeleton-block" style={{ height: '120px', marginBottom: '1rem' }} />
            <div className="lecture-grid">
              {[1, 2, 3, 4].map((n) => (
                <div key={n} className="skeleton skeleton-card" style={{ height: '220px' }} />
              ))}
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="lectures-page">
      <header className="lectures-page-header">
        <h1>Lectures</h1>
        <p>Explore spiritual discourses by topic and language</p>
      </header>

      {error && (
        <div className="lectures-page-error" role="alert">
          {error}
        </div>
      )}

      <div className="lectures-page-layout">
        <aside className="lectures-page-sidebar">
          <TopicSelector
            topics={topics}
            selectedTopicId={selectedTopicId}
            onSelect={handleTopicSelect}
          />
        </aside>

        <main className="lectures-page-main">
          {topics.length === 0 ? (
            <div className="empty-state-hero">
              <h2>No lectures available yet</h2>
              <p>Topics and lectures will appear here once they are added.</p>
            </div>
          ) : (
            <>
              <TopicDetail topic={selectedTopic} />
              {loadingLectures ? (
                <div className="lecture-grid">
                  {[1, 2, 3, 4].map((n) => (
                    <div key={n} className="skeleton skeleton-card" style={{ height: '220px' }} />
                  ))}
                </div>
              ) : lectures.length === 0 ? (
                <div className="lectures-empty">
                  <span style={{ fontSize: '2.5rem' }}>🎬</span>
                  <p>No lectures have been uploaded for this topic yet.</p>
                </div>
              ) : (
                <LectureGrid lectures={lectures} onLectureClick={handleLectureClick} />
              )}
            </>
          )}
        </main>
      </div>

      {lightboxOpen && lectures.length > 0 && (
        <VideoLightbox
          items={lectures}
          currentIndex={lightboxIndex}
          onClose={handleLightboxClose}
          onPrev={handleLightboxPrev}
          onNext={handleLightboxNext}
        />
      )}

      <footer className="lectures-page-footer">
        <p>
          Copyright: All content on this site is the property of Vedanta Center of Charlotte, Inc.
          Unauthorized duplication, distribution, or modification of this material is strictly prohibited.
        </p>
      </footer>
    </div>
  );
}
